import HTForm from '../../_shared/form/htform';
import { observable } from 'mobx';
import { rvalueRules, rValue2009StandardsWarning, glazing2009StandardsWarning } from '../../_shared/validation/validationhelpers';
import { AppStore } from '../../app/app.store';

export default class WallsForm extends HTForm {
    currentRoomNum;

    @observable era = null;
    @observable zone = null;
    @observable retrofitted2009 = false;
    @observable standards2009Compliant = null;
    @observable rValueEra = null;
    @observable numWindows = 0;
    @observable numDoors = 0;

    constructor(props) {
        super(props);
        this.currentRoomNum = props.currentRoomNum;

        // always add first wall
        const wallFields = this.getWallFields();
        this.$('walls').add(wallFields);
    }

    /**
     * Handle adding walls
     * @param value
     */
    handleIncrement(value) {
        // avoid adding the field if it already exists
        if (this.$('walls').has('wall' + value)) {
            return;
        }

        this.store.numWalls = value;
        const wallFields = this.getWallFields();
        this.$('walls').add(wallFields);
    }

    /**
     * Handle removing walls
     * @param num
     */
    handleRemove(num) {
        for (let i = this.store.numWalls; i > num; i -= 1) {
            this.del(`walls.wall${i}`);
        }

        this.store.numWalls = num;
    }

    /**
     * Handle incrementing windows. Allows for multiple added at a time
     * @param num
     * @param oldNum
     * @param wallNum
     * @param field - the field this is called from
     */
    incrementWindows(num, oldNum, wallNum, field) {
        const path = AppStore.getFieldPath(field.path, 2),
            start = oldNum + 1;

        let i;

        for (i = start; i <= num; i++) {
            this.addWindow(wallNum, i, path);
        }
    }

    /**
     * Handles removing windows, allowing for multiple to be removed at once
     * @param num
     * @param wallNum
     * @param field - the field this is called from
     */
    removeWindows(num, wallNum, field) {
        const path = AppStore.getFieldPath(field.path, 2),
            wallKey = `wall${wallNum}`,
            collection = this.$(`${path}.${wallKey}-windows`);

        for (let i = this.numWindows; i > num; i -= 1) {
            collection.del(`${wallKey}-window${i}`);
        }

        this.numWindows = num;
    }

    /**
     * Used when initing the form and from loops to add a single window at once.
     * @param wallNum
     * @param num
     * @param path
     */
    addWindow(wallNum, num, path) {
        const wallKey = `wall${wallNum}`,
            collection = this.$(`${path}.${wallKey}-windows`),
            exists = collection.has(`${wallKey}-window${num}`);

        if (exists) {
            return;
        }

        collection.add(this.getWindowFields(num, wallKey));

        this.numWindows = num;
    }

    /**
     * Handle incrementing doors. Allows for multiple added at a time
     * @param num
     * @param oldNum
     * @param wallNum
     * @param field - the field this is called from
     */
    incrementDoors(num, oldNum, wallNum, field) {
        const path = AppStore.getFieldPath(field.path, 2),
            start = oldNum + 1;

        let i;

        for (i = start; i <= num; i++) {
            this.addDoor(wallNum, i, path);
        }
    }

    /**
     * Handles removing doors, allowing for multiple to be removed at once
     * @param num
     * @param wallNum
     * @param field - the field this is called from
     */
    removeDoors(num, wallNum, field) {
        const path = AppStore.getFieldPath(field.path, 2),
            wallKey = `wall${wallNum}`,
            collection = this.$(`${path}.${wallKey}-doors`);

        for (let i = this.numDoors; i > num; i -= 1) {
            collection.del(`${wallKey}-door${i}`);
        }

        this.numDoors = num;
    }

    /**
     * Used when initing the form and from loops to add a single door at a time.
     * @param wall
     * @param num
     * @param path
     */
    addDoor(wall, num, path) {
        const wallKey = `wall${wall}`,
            collection = this.$(`${path}.${wallKey}-doors`),
            exists = collection.has(`${wallKey}-door${num}`);

        if (exists) {
            return;
        }

        collection.add(this.getDoorFields(num, wallKey));

        this.numDoors = num;
    }

    getWindowFields(num, wallKey) {
        const windowKey = `${wallKey}-window${num}`;

        return {
            label: `Window ${num}`,
            name: windowKey,
            type: 'fieldset',
            extra: {},
            fields: [
                {
                    name: `${windowKey}-3d-1a`,
                    type: 'areafield',
                    label: 'How big is the window?',
                    extra: {
                        description: () => {
                            return this.getDescription([
                                '<p>This helps us replace the right amount of wall surface with window surface.</p>',
                                `<p>${this.getHelpLink(9, 'Measuring windows', 'help-measure_window')}</p>`
                            ]);
                        }
                    },
                    fields: [
                        {
                            name: `${windowKey}-3d-1a-length`,
                            label: 'length',
                            type: 'numberfield',
                            extra: { unit: 'm' },
                            value: null,
                            placeholder: 'Length',
                            rules: 'required|numeric|greaterthan:0'
                        },
                        {
                            name: `${windowKey}-3d-1a-height`,
                            label: 'height',
                            type: 'numberfield',
                            extra: { unit: 'm' },
                            value: null,
                            placeholder: 'Height',
                            rules: 'required|numeric|greaterthan:0'
                        },
                        {
                            name: `${windowKey}-3d-1a-area`,
                            label: 'result',
                            type: 'numberfield',
                            extra: {
                                unit: 'm2',
                                actionName: 'window-edit-area'
                            },
                            value: null,
                            placeholder: 'Result',
                            rules: 'required|numeric|min:0'
                        }
                    ]
                },
                {
                    type: 'radiorvaluefield',
                    name: `${windowKey}-3d-1b`,
                    label: 'What type of glazing does the window have?',
                    extra: {
                        description: () => {
                            return this.getDescription([
                                '<p>This helps us understand how well the window retains heat.</p>',
                                `<p>${this.getHelpLink(10, 'Types of glazing', 'help-glazing_types')}</p>`
                            ]);
                        }
                    },
                    fields: [
                        {
                            name: `${windowKey}-3d-1b-1`,
                            type: 'radio',
                            value: this.standards2009Compliant ? 'double' : 'single',
                            label:
                                'What type of glazing does the window have?',
                            extra: {
                                options: () => {
                                    return [
                                        {
                                            value: 'single',
                                            label: 'Single glazing'
                                        },
                                        {
                                            value: 'double',
                                            label: 'Double glazing or better'
                                        }
                                    ];
                                },
                                warning: (show = false) => {
                                    return this.retrofitted2009 && show
                                        ? glazing2009StandardsWarning
                                        : false;
                                }
                            }
                        },
                        {
                            name: `${windowKey}-3d-1b-2`,
                            type: 'rvaluefield',
                            label: 'Estimated R-value',
                            value: null,
                            rules: rvalueRules.glazing,
                            extra: {
                                era: this.rValueEra,
                                zone: this.zone,
                                element: 'glazing',
                                glazing: 'single',
                                showHint: true,
                                warning: false,
                                showIf: {
                                    '===': [{ var: `${windowKey}-3d-1b-1` }, 'single']
                                }
                            }
                        },
                        {
                            name: `${windowKey}-3d-1b-3`,
                            type: 'rvaluefield',
                            label: 'Estimated R-value',
                            value: 0,
                            rules: rvalueRules.glazing,
                            extra: {
                                era: this.rValueEra,
                                zone: this.zone,
                                element: 'glazing',
                                glazing: 'double',
                                showHint: true,
                                warning: (show = false) => {
                                    return this.retrofitted2009 && show
                                        ? rValue2009StandardsWarning
                                        : false;
                                },
                                showIf: {
                                    '===': [{ var: `${windowKey}-3d-1b-1` }, 'double']
                                }
                            }
                        }
                    ]
                }
            ]
        };
    }

    getDoorFields(num, wallKey) {
        const doorKey = `${wallKey}-door${num}`;
        return {
            label: `Door ${num}`,
            name: doorKey,
            type: 'fieldset',
            extra: {},
            fields: [
                {
                    name: `${doorKey}-3e-1a`,
                    type: 'areafield',
                    label: 'What is the total area of the glass in the door?',
                    extra: {
                        description: () => {
                            return this.getDescription([
                                '<p>This helps us understand how well the glass retains heat.</p>',
                                `<p>${this.getHelpLink(11, 'Measuring glass panes in doors', 'help-measure_doors')}</p>`
                            ]);
                        }
                    },
                    fields: [
                        {
                            name: `${doorKey}-3e-1a-length`,
                            label: 'length',
                            type: 'numberfield',
                            extra: { unit: 'm' },
                            value: null,
                            placeholder: 'Length',
                            rules: 'required|numeric|greaterthan:0'
                        },
                        {
                            name: `${doorKey}-3e-1a-height`,
                            label: 'height',
                            type: 'numberfield',
                            extra: { unit: 'm' },
                            value: null,
                            placeholder: 'Height',
                            rules: 'required|numeric|greaterthan:0'
                        },
                        {
                            name: `${doorKey}-3e-1a-area`,
                            label: 'result',
                            type: 'numberfield',
                            extra: {
                                unit: 'm2',
                                actionName: 'doorglazing-edit-area'
                            },
                            value: null,
                            placeholder: 'Result',
                            rules: 'required|numeric|min:0'
                        }
                    ]
                },
                {
                    type: 'radiorvaluefield',
                    name: `${doorKey}-3e-1b`,
                    label: 'What type of glazing does this door have?',
                    extra: {
                        description: () => {
                            return this.getDescription([
                                '<p>This helps us understand how well the glass retains heat.</p>',
                                `<p>${this.getHelpLink(12, 'Types of glazing for glass panes in doors', 'help-glazing_types')}</p>`
                            ]);
                        }
                    },
                    fields: [
                        {
                            name: `${doorKey}-3e-1b-1`,
                            type: 'radio',
                            value: this.standards2009Compliant ? 'double' : 'single',
                            label:
                                'What type of glazing does this door have?',
                            extra: {
                                options: () => {
                                    return [
                                        {
                                            value: 'single',
                                            label: 'Single glazing'
                                        },
                                        {
                                            value: 'double',
                                            label: 'Double glazing or better'
                                        }
                                    ];
                                },
                                warning: (show = false) => {
                                    return this.retrofitted2009 && show
                                        ? glazing2009StandardsWarning
                                        : false;
                                }
                            }
                        },
                        {
                            name: `${doorKey}-3e-1b-2`,
                            type: 'rvaluefield',
                            label: 'Estimated R-value',
                            value: null,
                            rules: rvalueRules.glazing,
                            extra: {
                                era: this.rValueEra,
                                zone: this.zone,
                                element: 'glazing',
                                glazing: 'single',
                                showHint: false,
                                warning: false,
                                showIf: {
                                    '===': [{ var: `${doorKey}-3e-1b-1` }, 'single']
                                }
                            }
                        },
                        {
                            name: `${doorKey}-3e-1b-3`,
                            type: 'rvaluefield',
                            label: 'Estimated R-value',
                            value: null,
                            rules: rvalueRules.glazing,
                            extra: {
                                era: this.rValueEra,
                                zone: this.zone,
                                element: 'glazing',
                                glazing: 'double',
                                showHint: false,
                                warning: (show = false) => {
                                    return this.retrofitted2009 && show
                                        ? rValue2009StandardsWarning
                                        : false;
                                },
                                showIf: {
                                    '===': [{ var: `${doorKey}-3e-1b-1` }, 'double']
                                }
                            }
                        }
                    ]
                }
            ]
        };
    }

    getWallFields() {
        const num = this.store.numWalls,
            more = parseInt(num, 10) + 1,
            wallKey = `wall${num}`;

        return {
            name: wallKey,
            type: 'formsection',
            label: `Wall ${num} details`,
            fields: [
                {
                    name: `${wallKey}-3a`,
                    type: 'radio',
                    value: 'internal',
                    label:
                        'Is this wall internal, external or a mix of both?',
                    rules: 'required',
                    extra: {
                        options: () => {
                            return [
                                {
                                    value: 'internal',
                                    label: 'Internal'
                                },
                                {
                                    value: 'external',
                                    label: 'External'
                                }
                            ];
                        },
                        description: () => {
                            return this.getDescription([
                                '<p>This helps us understand how much heat could be lost through the wall. If a wall is a mix of internal and external divide it into an internal wall and an external wall. Enter the details for each part separately by adding another wall.</p>',
                                `<p>${this.getHelpLink(6, 'Understanding if a wall is internal or external', 'help-wall_types')}</p>`
                            ]);
                        }
                    },
                    observers: [
                        {
                            key: 'value',
                            call: ({ form, field, change }) => {
                                const path = AppStore.getFieldPath(field.path, 1);

                                if (change.newValue === 'internal') {
                                    const hasWindowsField = this.$(`${path}.${wallKey}-windowdetails.${wallKey}-3d-1a`),
                                        hasDoorsField = this.$(`${path}.${wallKey}-doordetails.${wallKey}-3e-1a`);
                                    hasWindowsField.set('value', 'no');
                                    hasDoorsField.set('value', 'no');
                                }

                                // update the action for the area field
                                this.$(`${path}.${wallKey}-3b.${wallKey}-3b-area`).extra.actionName = `wall-${change.newValue}-edit-area`;
                            }
                        }
                    ]
                },
                {
                    name: `${wallKey}-3b`,
                    type: 'areafield',
                    label: 'How big is the wall?',
                    extra: {
                        unit: 'm2',
                        description: () => {
                            return this.getDescription([
                                '<p>This helps us understand the size of the surface heat can escape through.</p>',
                                `<p>${this.getHelpLink(7, 'Measuring the area of a wall', 'help-measure_wall')}</p>`
                            ]);
                        }
                    },
                    fields: [
                        {
                            name: `${wallKey}-3b-length`,
                            label: 'length',
                            type: 'numberfield',
                            extra: { unit: 'm' },
                            value: null,
                            placeholder: 'Length',
                            rules: 'required|numeric|greaterthan:0'
                        },
                        {
                            name: `${wallKey}-3b-height`,
                            label: 'height',
                            type: 'numberfield',
                            extra: { unit: 'm' },
                            value: null,
                            placeholder: 'Height',
                            rules: 'required|numeric|greaterthan:0'
                        },
                        {
                            name: `${wallKey}-3b-area`,
                            label: 'result',
                            type: 'numberfield',
                            extra: {
                                unit: 'm2',
                                actionName: 'wall-internal-edit'
                            },
                            value: null,
                            placeholder: 'Result',
                            rules: 'required|numeric|min:0'
                        }
                    ]
                },
                {
                    name: `${wallKey}-3c-1`,
                    type: 'rvaluefield',
                    label: 'Wall insulation',
                    value: null,
                    rules: rvalueRules.wall,
                    extra: {
                        era: this.rValueEra,
                        zone: this.zone,
                        element: 'wall',
                        surface: 'external',
                        warning: (show = false) => {
                            return this.retrofitted2009 && show
                                ? rValue2009StandardsWarning
                                : false;
                        },
                        description: () => {
                            return this.getDescription([
                                '<p>Based on the age of this house, we can estimate whether the wall has insulation and how good it is (the R-value). Most houses do not have insulation in internal walls, so the tool assumes this.</p>'
                            ]);
                        },
                        showIf: {
                            '===': [{ var: `${wallKey}-3a` }, 'external']
                        }
                    }
                },
                {
                    name: `${wallKey}-3c-2`,
                    type: 'rvaluefield',
                    label: 'Wall insulation',
                    value: null,
                    rules: rvalueRules.wall,
                    extra: {
                        era: this.rValueEra,
                        zone: this.zone,
                        element: 'wall',
                        surface: 'internal',
                        warning: false,
                        description: () => {
                            return this.getDescription([
                                '<p>Based on the age of this house, we can estimate whether the wall has insulation and how good it is (the R-value). Most houses do not have insulation in internal walls, so the tool assumes this.</p>'
                            ]);
                        },
                        showIf: {
                            '===': [{ var: `${wallKey}-3a` }, 'internal']
                        }
                    }
                },
                {
                    name: `${wallKey}-windowdetails`,
                    type: 'fieldgroup',
                    label: 'Window details',
                    extra: {
                        showIf: {
                            '===': [{ var: `${wallKey}-3a` }, 'external']
                            // only show windows if wall is internal
                        }
                    },
                    fields: [
                        {
                            name: `${wallKey}-3d-1a`,
                            type: 'radio',
                            value: 'no',
                            label: 'Does this wall have any windows?',
                            rules: 'required',
                            description: () => {
                                return this.getDescription([
                                    '<p>This helps us understand more about the surfaces heat can escape through.</p>'
                                ]);
                            },
                            extra: {
                                options: () => {
                                    return [
                                        {
                                            value: 'no',
                                            label: 'No'
                                        },
                                        {
                                            value: 'yes',
                                            label: 'Yes'
                                        }
                                    ];
                                },
                                description: () => {
                                    return this.getDescription([
                                        '<p>This helps us understand more about the surfaces heat can escape through.</p>'
                                    ]);
                                }
                            },
                            observers: [
                                {
                                    key: 'value',
                                    call: ({ form, field, change }) => {
                                        const path = AppStore.getFieldPath(field.path, 1);
                                        if (change.newValue === 'yes') {
                                            this.incrementWindows(1, 0, num, field);
                                        } else {
                                            this.removeWindows(0, num, field);
                                            this.$(`${path}.${wallKey}-3d-1b`).set('value', 0);
                                        }
                                    }
                                }
                            ]
                        },
                        {
                            name: `${wallKey}-3d-1b`,
                            type: 'stepper',
                            value: 0,
                            label:
                                'How many windows does the wall have?',
                            rules: 'required',
                            extra: {
                                min: 1,
                                max: Infinity,
                                description: () => {
                                    return this.getDescription([
                                        `<p>${this.getHelpLink(8, 'Counting windows and panes', 'help-count_windows')}</p>`
                                    ]);
                                },
                                showIf: {
                                    '===': [{ var: `${wallKey}-3d-1a` }, 'yes']
                                }
                            },
                            observers: [
                                {
                                    key: 'value',
                                    call: ({ form, field, change }) => {
                                        if (change.newValue > change.oldValue) {
                                            this.incrementWindows(change.newValue, change.oldValue, num, field);
                                        } else {
                                            this.removeWindows(change.newValue, num, field);
                                        }
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    name: `${wallKey}-windows`,
                    type: 'collection',
                    fields: []
                },
                {
                    name: `${wallKey}-doordetails`,
                    type: 'fieldgroup',
                    label: 'Door details',
                    extra: {
                        showIf: {
                            '===': [{ var: `${wallKey}-3a` }, 'external']
                            // only show doors if wall is internal
                        }
                    },
                    fields: [
                        {
                            name: `${wallKey}-3e-1a`,
                            type: 'radio',
                            value: 'no',
                            label:
                                'Does this wall have any doors with glass panes?',
                            rules: 'required',
                            extra: {
                                options: () => {
                                    return [
                                        {
                                            value: 'no',
                                            label: 'No'
                                        },
                                        {
                                            value: 'yes',
                                            label: 'Yes'
                                        }
                                    ];
                                },
                                description: () => {
                                    return this.getDescription([
                                        '<p>This helps us understand more about the surfaces heat can escape through.</p>'
                                    ]);
                                }
                            },
                            observers: [
                                {
                                    key: 'value',
                                    call: ({ form, field, change }) => {
                                        const path = AppStore.getFieldPath(field.path, 1);
                                        if (change.newValue === 'yes') {
                                            this.incrementDoors(1, 0, num, field);
                                        } else {
                                            this.removeDoors(0, num, field);
                                            this.$(`${path}.${wallKey}-3e-1b`).set('value', 0);
                                        }
                                    }
                                }
                            ]
                        },
                        {
                            name: `${wallKey}-3e-1b`,
                            type: 'stepper',
                            value: 0,
                            label:
                                'How many doors with glass panes does the wall have?',
                            rules: 'required',
                            extra: {
                                min: 1,
                                max: Infinity,
                                showIf: {
                                    '===': [{ var: `${wallKey}-3e-1a` }, 'yes']
                                }
                            },
                            observers: [
                                {
                                    key: 'value',
                                    call: ({ form, field, change }) => {
                                        if (change.newValue > change.oldValue) {
                                            this.incrementDoors(change.newValue, change.oldValue, num, field);
                                        } else {
                                            this.removeDoors(change.newValue, num, field);
                                        }
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    name: `${wallKey}-doors`,
                    type: 'collection',
                    fields: []
                },
                {
                    name: `${wallKey}-addWall-section`,
                    type: 'formsection',
                    label: 'Add another wall?',
                    fields: [
                        {
                            name: `${wallKey}-addWall`,
                            type: 'radio',
                            label: 'Do you want to add another wall to your main living room?',
                            value: `${num}`,
                            extra: {
                                options: () => {
                                    return [
                                        {
                                            value: `${num}`,
                                            label: 'No'
                                        },
                                        {
                                            value: `${more}`,
                                            label: 'Yes'
                                        }
                                    ];
                                }
                            },
                            observers: [
                                {
                                    key: 'value', // can be any prop
                                    call: ({ form, field, change }) => {
                                        if (parseInt(field.value, 10) > parseInt(num, 10)) {
                                            this.handleIncrement(field.value);
                                        } else if (this.store.numWalls > parseInt(num, 10)) {
                                            this.handleRemove(num);
                                        }
                                    }
                                }
                            ]
                        }
                    ]
                }
            ]
        };
    }

    setup() {
        return {
            fields: [
                {
                    name: 'wallsintro',
                    type: 'formsection',
                    label: 'The walls in your main living room',
                    fields: [
                        {
                            name: 'wallsintrocontent',
                            type: 'content',
                            extra: {
                                description: () => {
                                    return this.getDescription([
                                        '<p>In this section we’ll ask you about each of the walls, including their size and features. Include the walls in any other space on this level that is always open to the main living room, like an open-plan kitchen, hallway or staircase. If you have a plan or the layout is complex, noting down which wall is which will help you keep track.</p>'
                                    ]);
                                }
                            }
                        }
                    ]
                },
                {
                    name: 'walls',
                    type: 'rootcollection',
                    fields: []
                }
            ]
        };
    }
}
